"use client";

import type { NewFormSession } from "@newform-monorepo/backend/src/lib/auth/NewFormSession";
import type React from "react";
import { createContext, useContext, useEffect } from "react";
import { identifyOrganization, identifyUser } from "@/lib/analytics";

const UserSessionContext = createContext<NewFormSession | null>(null);

/**
 * React context hook and provider for managing user session state in client component.
 *
 * The cache will refresh on page reload or redirect, so if the user logs out (which automatically redirects) the session will be clear.
 * @see {getUserSessionFromCookies} For server component usage
 * @returns {UserSession} Current user session context
 * @example How to use in a client component:
 * ```tsx
 * // Using the hook
 * function ProfileButton() {
 *   const userSession = useUserSession();
 *   return userSession.isAdmin ? <AdminPanel /> : <UserPanel />;
 * }
 *
 * // Using the provider
 * function App({ userSession }: { userSession: UserSession }) {
 *   return (
 *     <UserSessionProvider userSession={userSession}>
 *       <ProfileButton />
 *     </UserSessionProvider>
 *   );
 * }
 * ```
 */
export function useUserSession() {
  return useContext(UserSessionContext);
}

interface UserSessionProviderProps {
  userSession: NewFormSession | null;
  children: React.ReactNode;
}

export default function UserSessionProvider({
  userSession,
  children,
}: UserSessionProviderProps) {
  // Identify user to Segment when session data is available
  useEffect(() => {
    if (userSession?.userInfo) {
      identifyUser(userSession.userInfo.id, {
        email: userSession.userInfo.email,
        name: userSession.userInfo.name,
        organizationId: userSession.ownOrganization?.id || "",
        isAdmin: userSession.isAdmin,
        isAgencyUser: userSession.isAgencyUser,
      });
      identifyOrganization(userSession.ownOrganization?.id || "", {
        name: userSession.ownOrganization?.name || "",
        slug: userSession.ownOrganization?.slug || "",
        type: userSession.ownOrganization?.type || "",
        parentOrganizationId: userSession.parentOrganization?.id || "",
        logoUrl: userSession.ownOrganization?.logoUrl || "",
      });
    }
  }, [userSession]);

  return (
    <UserSessionContext.Provider value={userSession}>
      {children}
    </UserSessionContext.Provider>
  );
}
